import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow } from "@/components/Reveal";

const PROJECTS = [
  {
    title: "Almaguin Yard Solutions",
    category: "Trades & Property Services",
    url: "https://almaguinyardsolutions.ca/",
    image: "/work/almaguin-yard-solutions.jpg",
    alt: "Almaguin Yard Solutions homepage — excavation and landscaping in cottage country",
    description: "A lead-generating site for a premier excavation and landscaping crew serving Almaguin and Muskoka — ten services, a filterable project gallery, customer reviews, and a multi-service quote request built for customers calling from the job site.",
    built: ["Multi-service quote form", "Filterable work gallery", "Click-to-call on every page"],
  },
  {
    title: "Almaguin Highlands Chamber of Commerce",
    category: "Community & Non-Profit",
    url: "https://almaguinchamber.com/",
    image: "/work/almaguin-chamber.jpg",
    alt: "Almaguin Highlands Chamber of Commerce homepage — misty lake and pines",
    description: "A digital home for the region's business community — membership sign-up and benefits, a searchable local business directory, and news & events that keep members and visitors connected across the Highlands.",
    built: ["Membership & benefits pages", "Searchable business directory", "News & events hub"],
  },
  {
    title: "Kerby Stivene Leadership",
    category: "Professional Services",
    url: "https://kerbyleadership.com/",
    image: "/work/kerby-leadership.jpg",
    alt: "Kerby Stivene Leadership homepage — developing leaders for what comes next",
    description: "A polished coaching brand site for a Sundridge-based leadership consultant — workshops, keynotes, and one-on-one coaching presented with a book-a-call flow and a downloadable checklist that turns visitors into conversations.",
    built: ["Book-a-call enquiry flow", "Lead-magnet signup", "Partner & client showcase"],
  },
];

const FILTERS = ["All", "Trades & Property Services", "Community & Non-Profit", "Professional Services"];

const Work = () => {
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="work-page">
      <Seo
        title="Our Work | Good Work Web Co."
        description="Real websites we've built for Almaguin Highlands businesses and organizations — trades, community groups, and professional services, all live and working."
      />

      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Our Work</Eyebrow>
            <h1 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#233D35] mt-3 leading-[0.95] max-w-3xl" data-testid="work-heading">
              Websites we'd be proud to hang a sign on.
            </h1>
            <p className="mt-5 max-w-2xl text-[#77786F] text-base sm:text-lg leading-relaxed">
              Real sites for real neighbours. Every project below is live and working — click through to see how it feels in your hands.
            </p>
          </Reveal>

          <Reveal className="mt-10">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter projects" data-testid="work-filters">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  aria-pressed={filter === f}
                  className={`font-head font-semibold uppercase tracking-[0.12em] text-sm px-5 py-2.5 border transition-colors duration-200 ${
                    filter === f
                      ? "bg-[#233D35] text-[#F3EFE5] border-[#233D35]"
                      : "bg-white text-[#77786F] border-[#E2DACB] hover:border-[#233D35] hover:text-[#233D35]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-10 space-y-10" data-testid="work-grid">
            <AnimatePresence mode="popLayout">
              {visible.map((p, idx) => (
                <motion.article
                  key={p.title}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="group bg-white border border-[#E2DACB] shadow-[0_4px_20px_rgba(35,61,53,0.06)] grid grid-cols-1 lg:grid-cols-2"
                  data-testid={`project-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block overflow-hidden relative ${idx % 2 === 1 ? "lg:order-2" : ""}`}
                    data-testid={`project-link-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-label={`Visit ${p.title} (opens in a new tab)`}
                  >
                    <img
                      src={p.image}
                      alt={p.alt}
                      className="w-full h-64 sm:h-80 lg:h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 bg-[#C58A47] text-[#F3EFE5] font-head font-semibold uppercase tracking-[0.16em] text-[10px] px-2.5 py-1">
                      Live Site
                    </span>
                  </a>
                  <div className="p-6 sm:p-10 flex flex-col justify-center">
                    <span className="font-head font-bold uppercase tracking-[0.18em] text-[11px] text-[#C58A47]">{p.category}</span>
                    <h2 className="font-head font-bold uppercase tracking-tight text-2xl sm:text-3xl text-[#233D35] mt-2 leading-[0.95]">{p.title}</h2>
                    <p className="mt-4 text-sm sm:text-base text-[#77786F] leading-relaxed">{p.description}</p>
                    <ul className="mt-5 space-y-2">
                      {p.built.map((b) => (
                        <li key={b} className="flex items-center gap-2.5 text-sm text-[#314F64]">
                          <span className="w-1.5 h-1.5 rotate-45 bg-[#C58A47] shrink-0" aria-hidden="true" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7">
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`project-visit-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group/btn inline-flex items-center gap-2 border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-5 py-3 transition-colors duration-200"
                      >
                        Visit Live Site
                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                      </a>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <CtaBand title="Your business could be next on this page." />
    </div>
  );
};

export default Work;
